import {useCallback, useRef} from 'react';
import type {MouseEvent} from 'react';

/**
 * Backdrop click-away that ignores drags. A press that starts inside the dialog
 * and is released on the backdrop fires `click` on the backdrop (the nearest
 * common ancestor), so a bare onClick would close the modal mid-selection.
 * Spread the result on the backdrop element: it closes only when both the
 * mousedown and the click land on the backdrop itself.
 */
export function useBackdropDismiss(onDismiss: () => void) {
    const pressStartedOnBackdrop = useRef(false);

    const onMouseDown = useCallback((e: MouseEvent<HTMLElement>) => {
        pressStartedOnBackdrop.current = e.target === e.currentTarget;
    }, []);

    const onClick = useCallback(
        (e: MouseEvent<HTMLElement>) => {
            const startedOnBackdrop = pressStartedOnBackdrop.current;
            pressStartedOnBackdrop.current = false;
            if (startedOnBackdrop && e.target === e.currentTarget) onDismiss();
        },
        [onDismiss],
    );

    return {onMouseDown, onClick};
}
